/**
 * Seasonal-promotion label + hover ladder next to the PROMO source badge
 * (kanban a9d5de3b): "Freezban 1-3 pallets" with a tooltip listing every
 * rung (the earned rung highlighted), the cutoff date and the price-list
 * source the promotion undercut.
 *
 * The tooltip is portalled to document.body and positioned `fixed` from the
 * label's bounding box: the line-items table sits in an `overflow-x: auto`
 * wrapper, which also clips vertically, so an absolutely positioned tooltip
 * (the tiered-special pattern) loses its top rows on the first line. It
 * opens below the label when there is room, else above.
 */
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { LinePromotionContext } from '../../services/portalOrders';
import styles from './PromotionBadge.module.css';

interface PromotionBadgeProps {
  promo: LinePromotionContext;
  formatPrice: (val: string | number | null | undefined) => string;
}

interface TipPos {
  left: number;
  top: number;
  below: boolean;
}

const TIP_HEIGHT_GUESS = 170;

export default function PromotionBadge({ promo, formatPrice }: PromotionBadgeProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<TipPos | null>(null);

  const open = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const viewportH = window.innerHeight || 800;
    const below = r.bottom + 8 + TIP_HEIGHT_GUESS <= viewportH || r.top < TIP_HEIGHT_GUESS;
    setPos({ left: r.left + r.width / 2, top: below ? r.bottom + 8 : r.top - 8, below });
  }, []);
  const close = useCallback(() => setPos(null), []);

  const footer =
    (promo.cutoff_date ? `Through ${promo.cutoff_date}` : 'Seasonal promotion') +
    (promo.displaced_source && promo.displaced_price
      ? ` · instead of ${promo.displaced_source.replace(/_/g, ' ')} ${formatPrice(promo.displaced_price)}`
      : '');

  return (
    <span
      ref={anchorRef}
      className={styles.wrap}
      data-testid="promotion-badge"
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      tabIndex={0}
      aria-label={`${promo.label}: ${footer}`}
    >
      <span className={styles.label}>{promo.label}</span>
      {pos &&
        createPortal(
          <span
            className={`${styles.tooltip} ${pos.below ? styles.tooltipBelow : styles.tooltipAbove}`}
            style={{ left: pos.left, top: pos.top }}
            data-testid="promotion-ladder"
            role="tooltip"
          >
            <div className={styles.title}>Promotion{promo.note ? ` — ${promo.note}` : ''}</div>
            {promo.ladder.map((rung) => (
              <div
                key={rung.index}
                className={`${styles.row} ${rung.is_active ? styles.rowActive : ''}`}
                data-rung-active={rung.is_active ? 'true' : 'false'}
              >
                <span className={styles.range}>{rung.label}</span>
                <span className={styles.price}>{formatPrice(rung.price)}</span>
              </div>
            ))}
            <div className={styles.footer}>{footer}</div>
          </span>,
          document.body,
        )}
    </span>
  );
}
